import {
    Avatar,
    Button,
    Combobox,
    FluentProvider,
    Label,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Option,
    Spinner,
    makeStyles,
    tokens,
    typographyStyles,
    Text
} from "@fluentui/react-components";
import {
    ChevronDown16Regular,
    Dismiss16Regular,
    Dismiss20Regular,
    Info16Regular
} from "@fluentui/react-icons";
import * as React from "react";
import StringsStore from "sh-strings/store";
import ThemeUtils from "sh-application/utility/ThemeUtils";
import TimeZoneUtils from "sh-application/utility/TimeZoneUtils";
import { restartApp, UrlFactory } from "sh-application";

import { getGenericEventPropertiesObject } from "sh-instrumentation";
import teamSetupPickerStore, { setTeamSetupMembers } from "../store/store";
import TeamSetupPickerUtils from "../TeamSetupPickerUtils";
import { IScheduleProvisionMember } from "sh-models";
import { TeamSetupPickerStepProps } from "../TeamSetupPickerStepProps";
import { UserStore } from "sh-stores";
import { GraphService, InstrumentationService } from "sh-services";
import { UserProfile } from "@microsoft/shifts-models-data";

type MemberRole = "Member" | "Owner";

/** Debounce delay (ms) before issuing a tenant user search for the typed query. */
const SEARCH_DEBOUNCE_MS = 300;

/** Maximum number of suggestions to request from the tenant user search. */
const SEARCH_RESULTS_LIMIT = 10;

interface DraftMember {
    userId: string;
    displayName: string;
    email?: string;
    role: MemberRole;
    pinned?: boolean;
}

const ROLE_OPTIONS: MemberRole[] = ["Member", "Owner"];

const useStyles = makeStyles({
    // The card chrome (background, rounded corners, shadow) lives on `.root`. The wrapping
    // FluentProvider would otherwise paint its own opaque, square-cornered background of the
    // same color behind the card, making the rounded corners read as sharp. Keep it transparent.
    provider: {
        backgroundColor: "transparent",
        borderRadius: tokens.borderRadiusXLarge
    },
    root: {
        display: "flex",
        flexDirection: "column",
        rowGap: tokens.spacingVerticalS,
        padding: tokens.spacingHorizontalXXL,
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        border: `${tokens.strokeWidthThin} solid ${tokens.colorTransparentStroke}`,
        borderRadius: tokens.borderRadiusXLarge,
        boxShadow: tokens.shadow64,
        // Fill the (responsive) dialog surface and stay within the viewport so the card
        // reflows without horizontal scrolling down to a 320px-wide viewport (WCAG 1.4.10).
        // The members list inside scrolls when vertical space is constrained.
        width: "100%",
        maxWidth: "600px",
        height: "min(460px, 100vh)", // caps at the design height ($panel-height: 460px)
        maxHeight: "100vh",
        boxSizing: "border-box"
    },
    centerStatusSubstring: {
        color: tokens.colorNeutralForeground3,
        textAlign: "center"
    },
    header: {
        display: "flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalS,
        width: "100%"
    },
    title: {
        flex: "1 1 auto",
        minWidth: 0,
        color: tokens.colorNeutralForeground1,
        ...typographyStyles.subtitle1
    },
    body: {
        display: "flex",
        flexDirection: "column",
        rowGap: tokens.spacingVerticalS,
        width: "100%",
        flex: "1 1 auto",
        minHeight: 0
    },
    field: {
        display: "flex",
        flexDirection: "column",
        rowGap: tokens.spacingVerticalXXS,
        width: "100%",
        position: "relative"
    },
    input: {
        width: "100%"
    },
    // Cap the suggestion popover height to the pre-Fluent2 list length (180px). The Fluent v2
    // Combobox listbox otherwise grows much taller by default; this keeps it scrolling as before.
    suggestionListbox: {
        maxHeight: "180px"
    },
    // Lay the avatar and the name/email stack in a row inside each Combobox Option. The default
    // selection check-icon gutter is removed via `checkIcon={null}` so the avatar aligns left.
    suggestionOption: {
        display: "flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalS
    },
    suggestionStatus: {
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        color: tokens.colorNeutralForeground3,
        ...typographyStyles.caption1
    },
    suggestionText: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        flex: "1 1 auto"
    },
    suggestionName: {
        // Intentionally no explicit color: inherit the Option's foreground so the name follows
        // Fluent's hover color (colorNeutralForeground1Hover). Pinning colorNeutralForeground1 here
        // left the name white on the blue hover row in the high-contrast theme.
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...typographyStyles.body1
    },
    members: {
        display: "flex",
        flexDirection: "column",
        margin: 0,
        padding: 0,
        listStyle: "none",
        width: "100%",
        flex: "1 1 auto",
        minHeight: 0,
        overflowY: "auto"
    },
    memberRow: {
        display: "flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalS,
        width: "100%",
        height: "48px",
        flexShrink: 0,
        paddingLeft: tokens.spacingHorizontalSNudge,
        paddingRight: tokens.spacingHorizontalSNudge,
        boxSizing: "border-box",
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusMedium
    },
    memberText: {
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minWidth: 0,
        color: tokens.colorNeutralForeground2,
        paddingLeft: tokens.spacingHorizontalXXS
    },
    memberName: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...typographyStyles.body1
    },
    memberEmail: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...typographyStyles.caption1
    },
    memberActions: {
        display: "flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalXS,
        flexShrink: 0
    },
    roleButton: {
        ...typographyStyles.caption1,
        minWidth: "unset",
        paddingLeft: tokens.spacingHorizontalS,
        paddingRight: tokens.spacingHorizontalS
    },
    statusRow: {
        display: "flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalXS,
        padding: `${tokens.spacingVerticalS} 0`,
        color: tokens.colorNeutralForeground3,
        ...typographyStyles.caption1
    },
    errorRow: {
        padding: `${tokens.spacingVerticalS} 0`,
        color: tokens.colorPaletteRedForeground1,
        ...typographyStyles.caption1
    },
    centerStatus: {
        display: "flex",
        flex: "1 1 auto",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        rowGap: tokens.spacingVerticalXS,
        color: tokens.colorNeutralForeground1,
        ...typographyStyles.body1
    },
    footer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        columnGap: tokens.spacingHorizontalM,
        width: "100%",
        paddingTop: tokens.spacingVerticalM,
        marginTop: "auto"
    },
    footerInfo: {
        display: "flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalXS,
        color: tokens.colorNeutralForeground1,
        ...typographyStyles.caption1
    },
    infoIcon: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: tokens.colorNeutralForeground2
    },
    footerButtons: {
        display: "flex",
        alignItems: "center",
        columnGap: tokens.spacingHorizontalS
    }
});

const AddTeamMembers: React.FC<TeamSetupPickerStepProps> = (props) => {
    const styles = useStyles();
    const strings = StringsStore().registeredStringModules.get("teamSetupPicker")!.strings;
    const roleLabels: Record<MemberRole, string> = {
        Member: strings.get("memberRoleMember") || "",
        Owner: strings.get("memberRoleOwner") || ""
    };

    const [members, setMembers] = React.useState<DraftMember[]>([]);
    const [search, setSearch] = React.useState<string>("");
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [searchResults, setSearchResults] = React.useState<UserProfile[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<Error | null>(null);
    // Controls the suggestion popover. Combobox owns keyboard navigation/active-descendant; we only
    // gate whether it is open so an empty query never shows an empty dropdown.
    const [comboOpen, setComboOpen] = React.useState<boolean>(false);

    const trimmedSearch = search.trim();

    const logAddMembersAction = (action: string, draftMembers?: DraftMember[]) => {
        const eventData = [
            getGenericEventPropertiesObject(InstrumentationService.properties.EventType, action)
        ];
        if (draftMembers !== undefined) {
            const ownerCount = draftMembers.filter(m => m.role === "Owner").length;
            eventData.push(
                getGenericEventPropertiesObject(InstrumentationService.properties.NumberOfMembersAdded, draftMembers.length - ownerCount),
                getGenericEventPropertiesObject(InstrumentationService.properties.NumberOfOwnersAdded, ownerCount)
            );
        }
        InstrumentationService.logEvent(InstrumentationService.events.ConvergedOnboarding, eventData);
    };

    // Step-shown telemetry: emitted once when the "Add members" step is first rendered.
    React.useEffect(() => {
        logAddMembersAction(InstrumentationService.values.AddTeamMembersViewed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounced org-wide tenant user search. Each keystroke (after debounce) issues a
    // GraphService.searchTenantUsers call, aborting any in-flight request for a stale query.
    React.useEffect(() => {
        if (trimmedSearch.length === 0) {
            setSearchResults([]);
            setLoading(false);
            setError(null);
            return;
        }

        const abortController = new AbortController();
        setLoading(true);
        setError(null);

        const timeoutId = window.setTimeout(async () => {
            try {
                const users = await GraphService.searchTenantUsers({
                    filters: { displayNameStartsWith: trimmedSearch },
                    top: SEARCH_RESULTS_LIMIT,
                    abortController
                });
                if (!abortController.signal.aborted) {
                    setSearchResults(users);
                    setLoading(false);
                }
            } catch (err) {
                if (!abortController.signal.aborted) {
                    setError(err as Error);
                    setSearchResults([]);
                    setLoading(false);
                }
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timeoutId);
            abortController.abort();
        };
    }, [trimmedSearch]);

    const handleAddMember = (user: { userId: string; displayName: string; email?: string }) => {
        setMembers(prev => prev.some(m => m.userId === user.userId)
            ? prev
            : [...prev, { ...user, role: "Member" }]);
        setSearch("");
    };

    const handleRemoveMember = (userId: string) => {
        setMembers(prev => prev.filter(m => m.userId !== userId || m.pinned));
    };

    const handleRoleChange = (userId: string, role: MemberRole) => {
        setMembers(prev => prev.map(m =>
            m.userId === userId && !m.pinned ? { ...m, role } : m
        ));
    };

    const handleClose = () => {
        logAddMembersAction(InstrumentationService.values.AddTeamMembersClosed, members);
        props.onClose();
    };

    const handleAddClicked = () => {
        logAddMembersAction(InstrumentationService.values.AddTeamMembersAddClicked, members);
        void handleSubmit();
    };

    const handleSkipClicked = () => {
        logAddMembersAction(InstrumentationService.values.AddTeamMembersSkipClicked, members);
        void handleSubmit();
    };

    const handleSubmit = async () => {
        const provisionMembers: IScheduleProvisionMember[] = members.map(m => ({
            userId: m.userId,
            role: m.role.toLowerCase() as IScheduleProvisionMember["role"]
        }));
        setTeamSetupMembers(provisionMembers);

        const teamDisplayName = teamSetupPickerStore.teamSetupInfo?.teamDisplayName;
        const schedulingGroupName = strings.get("membersGroupName");
        const channelName = teamSetupPickerStore.teamSetupInfo?.channelName;
        const timeZoneOlsonCode = teamSetupPickerStore.teamSetupInfo?.timeZoneOlsonCode
            || TimeZoneUtils.getBrowserOlsonCode();

        setSubmitting(true);
        try {
            const createdScheduleTeamId = await TeamSetupPickerUtils.provisionScheduleTeam(
                null /* msTeamInfo - no existing MS team in the new-team flow */,
                timeZoneOlsonCode,
                {
                    teamDisplayName,
                    members: provisionMembers,
                    schedulingGroupName,
                    firstChannelName: channelName,
                    visibility: "Private"
                }
            );
            if (createdScheduleTeamId) {
                window.location.href = UrlFactory.getSchedulesUrl(createdScheduleTeamId);
                restartApp();
                return;
            }
            setSubmitting(false);
            props.onClose();
        } catch (error) {
            InstrumentationService.trackException(error, "AddTeamMembersProvisionScheduleTeam");
            TeamSetupPickerUtils.handleStaffHubServiceError(error as Parameters<typeof TeamSetupPickerUtils.handleStaffHubServiceError>[0]);
            setSubmitting(false);
            props.onClose();
        }
    };

    const suggestions = searchResults
        .map(user => ({
            userId: user.id,
            displayName: user.displayName,
            email: user.userPrincipalName
        }))
        .filter(s => !members.some(existing => existing.userId === s.userId));

    const showSuggestions = trimmedSearch.length > 0;

    if (submitting) {
        return (
            <FluentProvider theme={ThemeUtils.getFluentV2ShiftTheme()} className={styles.provider}>
                <div role="region" aria-label={strings.get("addMembersCreatingScheduleAriaLabel")} className={styles.root}>
                    <div className={styles.centerStatus}>
                        <Spinner
                            size="large"
                            label={strings.get("addMembersSettingUpGeneric")}
                            labelPosition="below"
                        />
                        <Text className={styles.centerStatusSubstring}>
                            {strings.get("unknownUserProvisioningSubstringForTeam")}
                        </Text>
                    </div>
                </div>
            </FluentProvider>
        );
    }

    return (
        <FluentProvider theme={ThemeUtils.getFluentV2ShiftTheme()} className={styles.provider}>
            <div role="region" aria-label={strings.get("addMembersRegionAriaLabel")} className={styles.root}>
                <div className={styles.header}>
                    <span className={styles.title}>{strings.get("addMembersTitle")}</span>
                    <Button
                        appearance="transparent"
                        size="small"
                        icon={<Dismiss20Regular />}
                        aria-label={strings.get("addMembersCloseAriaLabel")}
                        onClick={handleClose}
                    />
                </div>

                <div className={styles.body}>
                    <div className={styles.field}>
                        <Label htmlFor="add-members-input" size="small">
                            {strings.get("addMembersSearchLabel")}
                        </Label>
                        <Combobox
                            id="add-members-input"
                            className={styles.input}
                            appearance="filled-darker"
                            freeform
                            autoFocus
                            placeholder={strings.get("addMembersSearchPlaceholder")}
                            value={search}
                            // The picked person is added to the members list below, never kept as a
                            // persistent combobox selection — keep selectedOptions empty so no option
                            // renders as checked.
                            selectedOptions={[]}
                            open={comboOpen && showSuggestions}
                            // Disable autoSize so Fluent doesn't write an inline viewport-sized maxHeight
                            // onto the listbox (which overrides CSS). Our suggestionListbox cap then applies.
                            positioning={{ autoSize: false }}
                            onOpenChange={(_, data) => setComboOpen(data.open)}
                            onChange={event => {
                                setSearch(event.target.value);
                                setComboOpen(true);
                            }}
                            onOptionSelect={(_, data) => {
                                const picked = suggestions.find(s => s.userId === data.optionValue);
                                if (picked) {
                                    handleAddMember(picked);
                                }
                            }}
                            listbox={{ "aria-label": strings.get("addMembersSuggestionsAriaLabel"), className: styles.suggestionListbox }}
                        >
                            {loading && (
                                <Option key="__status" value="__status" disabled text="" className={styles.suggestionStatus}>
                                    <Spinner size="extra-small" label={strings.get("addMembersLoadingPeople")} labelPosition="after" />
                                </Option>
                            )}
                            {!loading && error && (
                                <Option key="__status" value="__status" disabled text="" className={styles.errorRow}>
                                    {strings.get("addMembersLoadSuggestionsError")!.format(error.message)}
                                </Option>
                            )}
                            {!loading && !error && suggestions.length === 0 && (
                                <Option key="__status" value="__status" disabled text="" className={styles.suggestionStatus}>
                                    {strings.get("addMembersNoMatches")}
                                </Option>
                            )}
                            {!loading && !error && suggestions.map(s => (
                                <Option
                                    key={s.userId}
                                    value={s.userId}
                                    text={s.displayName}
                                    checkIcon={null}
                                    className={styles.suggestionOption}
                                >
                                    <Avatar name={s.displayName} size={32} color="colorful" aria-hidden="true" />
                                    <span className={styles.suggestionText}>
                                        <span className={styles.suggestionName}>{s.displayName}</span>
                                        {s.email && <span className={styles.memberEmail}>{s.email}</span>}
                                    </span>
                                </Option>
                            ))}
                        </Combobox>
                    </div>

                    <ul className={styles.members} aria-label={strings.get("addMembersSelectedAriaLabel")}>
                        {members.map(m => (
                            <li key={m.userId} className={styles.memberRow}>
                                <Avatar
                                    name={m.pinned ? undefined : m.displayName}
                                    initials={m.pinned ? "Y" : undefined}
                                    size={32}
                                    color="colorful"
                                    aria-hidden="true"
                                />
                                <div className={styles.memberText}>
                                    <span className={styles.memberName}>
                                        {m.displayName}
                                        {m.pinned && ` ${strings.get("addMembersYouSuffix")}`}
                                    </span>
                                    {m.email && <span className={styles.memberEmail}>{m.email}</span>}
                                </div>
                                <div className={styles.memberActions}>
                                    <Menu>
                                        <MenuTrigger disableButtonEnhancement>
                                            <MenuButton
                                                appearance="transparent"
                                                size="small"
                                                disabled={m.pinned}
                                                menuIcon={<ChevronDown16Regular />}
                                                className={styles.roleButton}
                                                aria-label={strings.get("addMembersRoleAriaLabel")!.format(m.displayName)}
                                            >
                                                {roleLabels[m.role]}
                                            </MenuButton>
                                        </MenuTrigger>
                                        <MenuPopover>
                                            <MenuList>
                                                {ROLE_OPTIONS.map(role => (
                                                    <MenuItem
                                                        key={role}
                                                        onClick={() => handleRoleChange(m.userId, role)}
                                                    >
                                                        {roleLabels[role]}
                                                    </MenuItem>
                                                ))}
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>
                                    {!m.pinned && (
                                        <Button
                                            appearance="transparent"
                                            size="small"
                                            icon={<Dismiss16Regular />}
                                            aria-label={strings.get("addMembersRemoveAriaLabel")!.format(m.displayName)}
                                            onClick={() => handleRemoveMember(m.userId)}
                                        />
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className={styles.footer}>
                    <div className={styles.footerButtons}>
                        <Button appearance="secondary" onClick={handleSkipClicked}>{strings.get("addMembersSkipButton")}</Button>
                        <Button appearance="primary" onClick={handleAddClicked}>{strings.get("addMembersAddButton")}</Button>
                    </div>
                </div>
            </div>
        </FluentProvider>
    );
};

export default AddTeamMembers;
